import { ITableHeader, showServerError } from "@/utils";
import { createColumns, IColumnsGroup } from "@/utils/ColumnsCreator";
import { CreatedFilters } from "@/services/filters/filters";
import { IFilter } from "@/types";
import { filterClass } from "@/services/filters/filterClass";
import { getAdvertisersSearch } from "@/api/revenue";
import {
    getActiveUserSearch,
    getAffiliateSearch,
    getUserSearch,
} from "@/api/user";
import { getEmployeesActiveProducts, getEmployeesOffersV2 } from "@/api/offers";
import {
    translateGoalStatus,
    translateOffersStatus,
    translatePartnerStatus,
    translateThreePercentMarking,
} from "@/utils/translate";
import { IListItem, InfoCards, usePrepareInfoCards } from "@/components-UI/list/types";
import { PrivateRate } from "@/api/types/offers";
import store from "@/store";
import router from "@/router";
import { useRoute } from "vue-router/composables";
import { computed, ComputedRef } from "vue";
import {
    buildDefaultFiltersParamsObject,
    getDefaultFilterSelectValue,
    hasFilterDefaultValue,
    PrivateRatesScope,
    resolvePrivateRatesScope,
    shouldApplyDefaultFilter,
    withPrivateRatesScopeParams,
} from "@/services/TablePage/privateRatesFilterParams";

export const usePrivateRates = (): { getHeaders: ComputedRef<ITableHeader<keyof PrivateRate>[]>; getDefaultColumns: ComputedRef<string[]>; getColumns: () => IColumnsGroup[] } => {
    const headers: ITableHeader<keyof PrivateRate>[] = [
        { text: 'ID', value: 'id', align: 'center', width: '100px', fixed: true },
        { text: 'ID оффера', value: 'offer_id', width: '100px', align: 'center' },
        { text: 'Оффер', value: 'offer_name', width: 'auto' },
        { text: 'Статус оффера', value: 'offer_status' },
        { text: 'ID цели', value: 'goal_id', width: '100px', align: 'center' },
        { text: 'Цель', value: 'goal_name', width: 'auto' },
        { text: 'Статус цели', value: 'goal_is_active', width: 'auto' },
        { text: 'Рекламодатель', value: 'advertiser_id', width: 'auto' },
        { text: 'Менеджер рекламодателя', value: 'advertiser_manager_name', width: 'auto' },
        { text: 'Направление', value: 'department', width: 'auto' },
        { text: 'Тип маркировки', value: 'type', width: 'auto' },
        { text: 'ID партнёра', value: 'affiliate_id', width: '170px' },
        { text: 'Партнёр', value: 'affiliate_name', width: 'auto' },
        { text: 'Тип договора', value: 'affiliate_contract_type', width: 'auto' },
        { text: 'Статус партнёра', value: 'affiliate_status', width: '100px' },
        { text: 'Менеджер партнёра', value: 'affiliate_manager_name', width: 'auto' },
        { text: 'Входящая цена', value: 'full_price', align: 'right', width: '105px' },
        { text: 'Входящая цена без НДС', value: 'price_after_vat', align: 'right', lineBreak: true, width: '105px' },
        { text: 'Доход для немарк. трафика', value: 'income', align: 'right', lineBreak: true, width: '115px' },
        { text: 'Доход для марк. трафика', value: 'revenue', align: 'right', lineBreak: true, width: '115px' },
        { text: 'Дата начала', value: 'start_date', align: 'right', width: '150px' },
        { text: 'Дата окончания', value: 'end_date', align: 'right', width: '150px' },
        { text: 'Статус', value: 'rate_status', width: '100px' },
        { text: 'Дата создания', value: 'created_at', align: 'right', width: '150px' },
        { text: 'Дата обновления', value: 'updated_at', align: 'right', width: '150px' },
    ];

    const defaultColumns = [
        'id',
        'offer_id',
        'offer_name',
        'offer_status',
        'goal_id',
        'goal_name',
        'goal_status',
        'marking',
        'affiliate_id',
        'affiliate_name',
        'affiliate_status',
        'full_price',
        'price_after_vat',
        'incoming',
        'revenue',
        'start_date',
        'end_date',
        'goal_is_active',
        'type',
        'affiliate_id',
        'income',
    ];

    const hideOnOffer = ['offer_id', 'offer_name', 'offer_status', 'advertiser_id', 'advertiser_manager_name', 'departments'];
    const hideOnAffiliate = ['affiliate_id', 'affiliate_name', 'affiliate_contract_type', 'affiliate_status', 'affiliate_manager_name'];

    const route = useRoute();
    const isOffer = computed(() => route.name === 'offer');
    const isAffiliate = computed(() => route.name === 'affiliate');

    const getDefaultColumns = computed(() => {
        if (isOffer.value) return defaultColumns.filter(col => !hideOnOffer.includes(col));
        if (isAffiliate.value) return defaultColumns.filter(col => !hideOnAffiliate.includes(col));
        return defaultColumns;
    });

    const getHeaders = computed(() => {
        if (isOffer.value) return headers.filter(h => !hideOnOffer.includes(h.value));
        if (isAffiliate.value) return headers.filter(h => !hideOnAffiliate.includes(h.value));
        return headers;
    });

    const getColumns = (): IColumnsGroup[] => {
        return createColumns(getHeaders.value, getDefaultColumns.value);
    };

    return { getHeaders, getDefaultColumns, getColumns };
};

export class PrivateRatesFilters extends CreatedFilters {
    constructor() {
        super();
        this.loadProductTypes();
    }

    private async loadProductTypes(): Promise<void> {
        const filter = this.filters.find((f) => f.id === 'product_id');
        if (!filter) return;
        try {
            filter.items = await getEmployeesActiveProducts();
        } catch (err) {
            showServerError(err, 'Список типов продуктов не загружен');
        }
    }

    getFilters(): IFilter[] {
        const currentUser = store.getters['authModule/GET_USER'];
        const changedUser = {
            email: currentUser.email,
            first_name: currentUser.first_name,
            id: currentUser.account_id,
            last_name: currentUser.last_name,
            user_id: currentUser.id,
            name: `${currentUser.account_id} ${currentUser.first_name} ${currentUser.last_name}`,
        };
        const currentUserIsManager = currentUser.roles.includes('ROLE_ACCOUNT_MANAGER');
        const isAffiliate = this.checkRoute('affiliate');
        const affiliate = store.getters['GET_CURRENT_AFFILIATE'];
        const isOffer = this.checkRoute('offer');
        const offer = store.getters['offersModule/GET_CURRENT_OFFER'];

        return [
            new filterClass.Autocomplete({
                col: 1,
                id: 'advertiser_id',
                apiMethod: getAdvertisersSearch,
                label: 'Рекламодатель',
                itemValue: 'id',
                itemText: 'name',
                select: [],
                items: [],
                multiple: true,
                multiPaste: true,
                clearable: true,
                isHidden: isOffer,
            }),
            new filterClass.Autocomplete({
                col: 1,
                id: 'advertiser_manager_id',
                label: 'Менеджер рекламодателя',
                apiMethod: getActiveUserSearch,
                select: currentUserIsManager ? [changedUser] : [],
                items: currentUserIsManager ? [changedUser] : [],
                itemText: 'name',
                itemValue: 'id',
                multiple: true,
                multiPaste: true,
                minSearchLength: 2,
                clearable: true,
                hideNoDataAutocomplete: false,
                type: "number",
                isHidden: isOffer,
                pinCurrentUser: true,
            }),
            new filterClass.Autocomplete({
                col: 1,
                id: 'offer_id',
                apiMethod: getEmployeesOffersV2,
                key: 'search',
                label: 'Оффер',
                itemValue: 'legacy_id',
                itemText: 'name',
                select: isOffer ? [offer] : [],
                defaultValue: isOffer ? [offer] : [],
                items: [],
                multiple: true,
                multiPaste: true,
                clearable: true,
                isHidden: isOffer,
            }),
            new filterClass.Multiselect({
                col: 1,
                id: 'offer_status',
                label: 'Статус оффера',
                itemValue: 'id',
                clearable: true,
                type: 'string',
                select: [],
                defaultValue: isOffer ? [] : ['active'],
                items: [
                    { id: 'active' },
                    { id: 'paused' },
                    { id: 'deleted' },
                    { id: 'pending' },
                    { id: 'expired' },
                ],
                template: ({ id }) => (this.translateStatus(id, translateOffersStatus)),
                itemText: ({ id }) => (this.translateStatus(id, translateOffersStatus)),
                isHidden: isOffer,
            }),
            new filterClass.Multiselect({
                col: 1,
                id: 'goal_id',
                itemValue: 'legacy_id',
                itemText: (item: any): string => `${item.legacy_id} ${item.name}`,
                label: 'Цель',
                noDataText: 'Выберите оффер',
                items: [],
                clearable: true,
                disabled: true,
                type: 'number',
                template: (item: any): string => `${item.legacy_id} ${item.name}`,
                tooltip: 'Сначала отфильтруйте по офферу',
            }),
            new filterClass.Select({
                col: 1,
                id: 'goal_is_active',
                label: 'Статус цели',
                type: 'boolean',
                select: '',
                defaultValue: 'true',
                items: [{ value: true }, { value: false }],
                itemValue: 'value',
                clearable: true,
                remove: true,
                translate: (i) => (translateGoalStatus(i?.value ?? i).text),
                itemText: (i) => (translateGoalStatus(i?.value ?? i).text),
            }),
            new filterClass.Multiselect({
                col: 1,
                label: 'Направление',
                id: 'offer_department',
                type: 'number',
                select: [],
                clearable: true,
                items: [
                    { name: 'Россия', id: 1 },
                    { name: 'Казахстан', id: 3 },
                    { name: 'Мир', id: 4 },
                ],
                itemValue: 'id',
                itemText: 'name',
                template: (i): string => i.name,
            }),
            new filterClass.Multiselect({
                col: 1,
                id: 'product_id',
                label: 'Тип продукта',
                type: 'number',
                select: [],
                items: [],
                clearable: true,
                itemValue: 'id',
                itemText: 'name',
                template: (item): string => item.name,
                noDataText: 'Нет выбранных типов продукта',
            }),
            new filterClass.Select({
                col: 1,
                id: 'type',
                label: 'Тип маркировки',
                type: 'string',
                clearable: true,
                select: '',
                defaultValue: '',
                items: ['above', 'from_rate', 'from_rate_always'],
                translate: (i): string => translateThreePercentMarking(i),
            }),
            new filterClass.Autocomplete({
                col: 2,
                id: 'affiliate_id',
                label: 'Партнёр',
                apiMethod: getAffiliateSearch,
                itemValue: 'id',
                itemText: 'first_name',
                template: (i) => `${i.id} ${i.first_name} ${i.last_name}`,
                select: isAffiliate ? [affiliate] : [],
                defaultValue: isAffiliate ? [affiliate] : [],
                items: [],
                clearable: true,
                multiple: true,
                multiPaste: true,
                isHidden: isAffiliate,
            }),
            new filterClass.Multiselect({
                col: 2,
                id: isOffer ? 'affiliate_status_or_default' : 'affiliate_status',
                label: 'Статус партнёра',
                type: 'string',
                items: [{ id: 'active' }, { id: 'pending' }, { id: 'deleted' }, { id: 'blocked' }, { id: 'rejected' }],
                select: [],
                defaultValue: [],
                itemValue: 'id',
                clearable: true,
                multiPaste: true,
                template: ({ id }) => this.translateStatus(id, translatePartnerStatus),
                itemText: ({ id }) => this.translateStatus(id, translatePartnerStatus),
                isHidden: isAffiliate,
            }),
            new filterClass.Autocomplete({
                col: 2,
                id: 'affiliate_manager_id',
                label: 'Менеджер партнёра',
                apiMethod: getUserSearch,
                itemValue: 'id',
                itemText: 'name',
                template: (i) => `${i.id} ${i.first_name} ${i.last_name}`,
                select: [],
                items: [],
                clearable: true,
                isHidden: isAffiliate,
                pinCurrentUser: true,
            }),
            new filterClass.Multiselect({
                col: 2,
                id: 'rate_status',
                label: 'Статус цены',
                type: 'string',
                clearable: true,
                select: '',
                defaultValue: ['active', 'planned'],
                items: [
                    { name: 'Актуальная', id: 'active' },
                    { name: 'Не актуальная', id: 'inactive' },
                    { name: 'Запланирована', id: 'planned' },
                ],
                itemValue: 'id',
                itemText: 'name',
                template: (item) => item.name,
            }),
            new filterClass.Select({
                col: 2,
                id: 'is_default',
                label: 'Цена по умолчанию',
                type: 'boolean',
                clearable: true,
                remove: true,
                select: '',
                items: [{ value: true }, { value: false }],
                itemValue: 'value',
                translate: (i): string => (i?.value ?? i) ? 'Да' : 'Нет',
            }),
            new filterClass.Datepicker({
                col: 2,
                id: 'start',
                label: 'Дата начала',
                pickerType: 'date',
                clearable: true,
            }),
            new filterClass.Datepicker({
                col: 2,
                id: 'end',
                label: 'Дата окончания',
                pickerType: 'date',
                clearable: true,
            }),
        ];
    }

    getPrivateRatesScope(): PrivateRatesScope {
        const offer = store.getters['offersModule/GET_CURRENT_OFFER'];
        const affiliate = store.getters['GET_CURRENT_AFFILIATE'];

        return resolvePrivateRatesScope({
            routeName: router.currentRoute.name,
            routeParamId: router.currentRoute.params.id,
            offerLegacyId: offer?.legacy_id ?? offer?.id ?? null,
            affiliateId: affiliate?.id ?? null,
        });
    }

    private buildAndCommitDefaultFilters(clearBeforeReset: boolean): Record<string, unknown> {
        const queryParams = router.currentRoute.query as Record<string, unknown>;
        const routeName = router.currentRoute.name;

        this.filters.forEach((filter) => {
            if (clearBeforeReset) {
                filter.clear();
            }

            if (!shouldApplyDefaultFilter(filter.id, queryParams, routeName)) {
                return;
            }
            if (!hasFilterDefaultValue(filter.defaultValue)) {
                return;
            }

            if (clearBeforeReset && filter.autocomplete && !filter.multiple) {
                const defaultValue = getDefaultFilterSelectValue(filter);
                filter.select = filter.items?.find(
                    (i) => i[filter.itemValue!] === defaultValue,
                ) || '';
                return;
            }

            filter.select = getDefaultFilterSelectValue(filter);
        });

        const paramsObj = buildDefaultFiltersParamsObject(this.filters, queryParams, routeName);
        const scoped = withPrivateRatesScopeParams(paramsObj, this.getPrivateRatesScope());

        if (queryParams.columns) {
            scoped.columns = queryParams.columns;
        }

        store.commit('SET_FILTERS_PARAMS_OBJECT', scoped);
        store.commit('SET_DEFAULT_FILTERS_PARAMS_OBJECT', { ...scoped });

        return scoped;
    }

    applyDefaultFilters(): Record<string, unknown> {
        return this.buildAndCommitDefaultFilters(false);
    }

    clearFilters(): void {
        const scoped = this.buildAndCommitDefaultFilters(true);
        store.commit('revenueModule/SET_ADVERTISER_FIELDS', {});
        this.updateQueryString(scoped);
    }

    getActiveFiltersToSend(): Record<string, unknown> {
        const sentParams = super.getActiveFiltersToSend() as Record<string, unknown>;
        const scope = this.getPrivateRatesScope();

        if (scope.offerId != null) {
            const offer = store.getters['offersModule/GET_CURRENT_OFFER'];
            const offerFilter = this.filters.find((f) => f.id === 'offer_id');
            if (offerFilter && (!Array.isArray(offerFilter.select) || !offerFilter.select.length) && offer) {
                offerFilter.select = [offer];
            }
        }

        if (scope.affiliateId != null) {
            const affiliate = store.getters['GET_CURRENT_AFFILIATE'];
            const affiliateFilter = this.filters.find((f) => f.id === 'affiliate_id');
            if (affiliateFilter && (!Array.isArray(affiliateFilter.select) || !affiliateFilter.select.length) && affiliate) {
                affiliateFilter.select = [affiliate];
            }
        }

        return withPrivateRatesScopeParams(sentParams, scope);
    }

    updateQueryString(data: Record<string, unknown>): void {
        const currentQuery = router.currentRoute.query;
        const query: Record<string, unknown> = { ...data };

        if (currentQuery.tab) {
            query.tab = currentQuery.tab;
        }
        if (currentQuery.columns) {
            query.columns = currentQuery.columns;
        }
        if (currentQuery.sort_by) {
            query.sort_by = currentQuery.sort_by;
        }
        if (currentQuery.sort_desc !== undefined) {
            query.sort_desc = currentQuery.sort_desc;
        }

        router.replace({
            name: router.currentRoute.name || '',
            params: { ...router.currentRoute.params },
            query: query as Record<string, string | (string | null)[]>,
        }).catch((error) => {
            if (error.name !== 'NavigationDuplicated') {
                throw error;
            }
        });
    }
}

type RateItem = IListItem<PrivateRate>
type RateCardName = 'info' | 'income' | 'author';
export type RateCards = {
    [key in RateCardName]: RateItem[]
}

export const usePrivateRateSingle = (data: PrivateRate): { getCards: () => InfoCards<PrivateRate> } => {
    const info: RateItem[] = [
        {
            itemType: 'route',
            label: 'Оффер',
            routeName: 'offer',
            routeText: 'offer_id',
            additionalText: 'offer_name',
            isCopyTooltip: true,
            value: '',
            valueKey: 'offer_id',
            status: (i: PrivateRate) => translateOffersStatus(i.offer_status),
        },
        {
            itemType: 'boolean',
            label: 'НДС',
            value: '',
            valueKey: 'vat',
            trueText: 'Да',
            falseText: 'Нет',
        },
        {
            itemType: 'text',
            label: 'Тип маркировки',
            value: '',
            valueKey: 'type',
            template: (type: string) => translateType(type),
            valueSlot: 'type',
        },
        {
            itemType: 'route',
            label: 'Цель',
            routeName: 'offer',
            routeText: 'goal_id',
            additionalText: 'goal_name',
            isCopyTooltip: true,
            value: '',
            valueKey: 'offer_id',
            status: (i: PrivateRate) => translateGoalStatus(i.goal_is_active),
        },
        {
            itemType: 'route',
            label: 'Рекламодатель',
            routeName: 'advertiser',
            routeText: 'advertiser_id',
            additionalText: 'advertiser_name',
            isCopyTooltip: true,
            value: '',
            valueKey: 'advertiser_id',
        },
        {
            itemType: 'text',
            label: 'Менеджер рекламодателя',
            value: '',
            valueKey: 'advertiser_manager_name',
        },
        {
            itemType: 'text',
            label: 'Направление',
            value: '',
            valueKey: 'departments',
            valueSlot: 'departments',
        },
        {
            valueSlot: 'is_default',
            label: '',
            value: '',
            isHideLabel: true,
            isHidden: true,
        },
        {
            itemType: 'route',
            label: 'Партнёр',
            routeName: 'affiliate',
            routeText: 'affiliate_id',
            additionalText: 'affiliate_name',
            isCopyTooltip: true,
            value: '',
            valueKey: 'affiliate_id',
            status: (i: PrivateRate) => translatePartnerStatus(i.affiliate_status),
            valueSlot: 'affiliate',
        },
        {
            itemType: 'text',
            label: 'Тип договора партнёра',
            value: '',
            valueKey: 'affiliate_contract_type',
            valueSlot: 'affiliate_contract_type',
        },
        {
            itemType: 'text',
            label: 'Менеджер партнёра',
            value: '',
            valueKey: 'affiliate_manager_name',
            valueSlot: 'affiliate_manager_name',
        },
    ];

    const income: RateItem[] = [
        {
            itemType: 'number',
            label: 'Входящая цена',
            value: '',
            valueKey: 'full_price',
            valueSlot: 'full_price',
        },
        {
            itemType: 'number',
            label: 'Входящая цена без НДС',
            value: '',
            valueKey: 'price_after_vat',
            valueSlot: 'price_after_vat',
        },
        {
            itemType: 'number',
            label: 'Доход для немаркируемого трафика',
            value: '',
            valueKey: 'income',
            valueSlot: 'income',
        },
        {
            itemType: 'number',
            label: 'Доход для маркируемого трафика',
            value: '',
            valueKey: 'revenue',
            valueSlot: 'revenue',
        },
        {
            itemType: 'date',
            label: 'Дата начала',
            value: '',
            valueKey: 'start_date',
            valueSlot: 'start_date',
            isSeconds: true,
            isShowTime: true,
        },
        {
            itemType: 'date',
            label: 'Дата окончания',
            value: '',
            valueKey: 'end_date',
            valueSlot: 'end_date',
            isSeconds: true,
            isShowTime: true,
        },
    ];

    const author: RateItem[] = [
        {
            itemType: 'text',
            label: 'Автор',
            value: '',
            valueKey: 'created_by',
        },
        {
            itemType: 'date',
            label: 'Дата создания',
            value: '',
            valueKey: 'created_at',
            valueSlot: 'created_at',
            isSeconds: true,
            isShowTime: true,
        },
        {
            itemType: 'date',
            label: 'Дата обновления',
            value: '',
            valueKey: 'updated_at',
            valueSlot: 'updated_at',
            isSeconds: true,
            isShowTime: true,
        },
    ];

    const cards: RateCards = {
        info,
        income,
        author,
    };

    const translateType = (type: string) => {
        switch (type) {
        case 'above':return 'Сверху';
        case 'from_rate': return 'Из ставки';
        case 'from_rate_always': return 'Всегда из ставки';
        case 'none': return 'Не учитывать';
        default: return '';
        }
    };

    const getCards = () => {
        return usePrepareInfoCards<PrivateRate>(cards, data);
    };

    return { getCards };
};
